import {
  ArrowLeft,
  Award,
  BarChart3,
  ChevronRight,
  Filter,
  History,
  Loader2,
  PauseCircle,
  PlayCircle,
  RotateCcw,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '../../constants/routes';
import { useCompanySports } from '../../hooks/useCompanySports';
import {
  useCompanyPromotionHistory,
  useRankDistribution,
} from '../../hooks/belts/useRankViews';

/**
 * Owner-facing promotion history + gym-wide rank distribution (#750).
 */
export default function BeltHistoryPage() {
  const sports = useCompanySports();

  const [sportId, setSportId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const filters = useMemo(
    () => ({
      sportId: sportId || undefined,
      startDate: startDate || undefined,
      endDate: endDate ? `${endDate}T23:59:59` : undefined,
    }),
    [sportId, startDate, endDate]
  );

  const history = useCompanyPromotionHistory(filters);
  const distribution = useRankDistribution(sportId || undefined);

  const resetFilters = () => {
    setSportId('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <Link
        to={ROUTES.BELTS}
        className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" /> Back to profiles
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-[#EBFFEE] flex items-center gap-2">
          <History className="w-6 h-6 text-emerald-400" /> Belt history &amp; distribution
        </h1>
        <p className="text-sm text-white/60 mt-1">
          Every promotion (and transfer setup) across your gym, plus how your
          students are distributed across the ranks of each discipline.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Filter className="w-4 h-4 text-white/40" />
        <select
          value={sportId}
          onChange={(e) => setSportId(e.target.value)}
          className="rounded-md bg-white/5 border border-white/10 px-2 py-1 text-sm text-[#EBFFEE]"
        >
          <option value="">All disciplines</option>
          {(sports.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.sport_name}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="rounded-md bg-white/5 border border-white/10 px-2 py-1 text-sm text-[#EBFFEE]"
          placeholder="From"
        />
        <span className="text-white/40">→</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="rounded-md bg-white/5 border border-white/10 px-2 py-1 text-sm text-[#EBFFEE]"
          placeholder="To"
        />
        {(sportId || startDate || endDate) && (
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white"
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-[#EBFFEE] flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> Rank distribution
        </h2>
        {distribution.isLoading ? (
          <div className="text-sm text-white/60 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (distribution.data ?? []).length === 0 ? (
          <div className="text-sm text-white/50 rounded-md border border-white/10 bg-white/[0.02] p-4">
            No ranked students {sportId ? 'in this discipline' : 'yet'}.
          </div>
        ) : (
          <div className="rounded-lg border border-white/10 overflow-hidden">
            <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 bg-white/[0.04] text-xs uppercase tracking-wide text-white/50">
              <span>Discipline · rank</span>
              <span className="text-right">Active</span>
              <span className="text-right">On hold</span>
            </div>
            {(distribution.data ?? []).map((d) => (
              <div
                key={d.rank_id}
                className="grid grid-cols-[1fr_auto_auto] gap-2 items-center px-3 py-2 border-t border-white/5 text-sm"
              >
                <div className="min-w-0">
                  <span className="text-white/60">{d.sport_name}</span>
                  <span className="text-white/30 mx-1.5">·</span>
                  <span className="text-[#EBFFEE]">{d.rank_name}</span>
                  {d.profile_name && (
                    <span className="text-xs text-white/40 ml-2">{d.profile_name}</span>
                  )}
                </div>
                <span className="inline-flex items-center gap-1 text-emerald-300 justify-end">
                  <PlayCircle className="w-3.5 h-3.5" /> {d.active_count}
                </span>
                <span className="inline-flex items-center gap-1 text-amber-300 justify-end">
                  <PauseCircle className="w-3.5 h-3.5" /> {d.on_hold_count}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-[#EBFFEE] flex items-center gap-2">
          <Award className="w-4 h-4" /> Promotion log
        </h2>

        {history.isLoading ? (
          <div className="text-sm text-white/60 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (history.data ?? []).length === 0 ? (
          <div className="text-sm text-white/50 rounded-md border border-white/10 bg-white/[0.02] p-4">
            No promotions match the current filters.
          </div>
        ) : (
          <div className="rounded-lg border border-white/10 divide-y divide-white/5">
            {(history.data ?? []).map((p) => (
              <div key={p.id} className="px-4 py-3 hover:bg-white/[0.02]">
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <Link
                    to={ROUTES.STUDENT_BELT_RANKS.replace(':studentId', p.student_id)}
                    className="font-medium text-[#EBFFEE] hover:underline"
                  >
                    {p.student_name ?? p.student_id}
                  </Link>
                  <span className="text-white/40">·</span>
                  <span className="text-white/60">{p.sport_name}</span>
                  {p.is_transfer_setup && (
                    <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border border-blue-500/40 text-blue-300 bg-blue-500/10">
                      transfer setup
                    </span>
                  )}
                </div>
                <div className="text-xs text-white/60 mt-1 flex items-center gap-1.5 flex-wrap">
                  <span className="text-white/70">{p.from_rank_name ?? '—'}</span>
                  <ChevronRight className="w-3 h-3 text-white/40" />
                  <span className="text-emerald-300">{p.to_rank_name}</span>
                  <span className="text-white/40">·</span>
                  <span>{new Date(p.promoted_at).toLocaleString()}</span>
                  {p.promoter_name && (
                    <>
                      <span className="text-white/40">·</span>
                      <span>by {p.promoter_name}</span>
                    </>
                  )}
                </div>
                {p.notes && (
                  <div className="text-xs text-white/50 mt-1 italic">&ldquo;{p.notes}&rdquo;</div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
